import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Music2, FileText, FolderOpen } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const TYPE_META = {
  piece: { label: "fav.type_piece", icon: Music2 },
  version: { label: "fav.type_version", icon: Music2 },
  file: { label: "fav.type_file", icon: FileText },
  document: { label: "fav.type_document", icon: FolderOpen },
};

export default function FavorisPage() {
  const { t } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/favorites").then((r) => setFavorites(r.data)).catch((e) => toast.error(e.friendly));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    await api.delete(`/favorites/${id}`).catch((e) => toast.error(e.friendly));
    load();
  };

  const open = (f) => {
    navigate(f.path || (f.target_type === "document" ? "/documents" : "/bibliotheque"));
  };

  const grouped = ["piece", "version", "file", "document"].map((t) => ({
    type: t,
    icon: TYPE_META[t].icon,
    items: favorites.filter((f) => f.target_type === t),
  }));

  return (
    <div className="space-y-6" data-testid="favorites-page">
      <div>
        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
          <Heart className="h-6 w-6 text-rose-400 fill-rose-400" /> {t("fav.title")}
      </h2>
        <p className="text-sm t-secondary mt-1">{t("fav.subtitle")}</p>
      </div>
      {favorites.length === 0 && (
        <p className="t-secondary text-sm app-card p-6 text-center" data-testid="no-favorites">
          {t("fav.empty")}
        </p>
      )}
      {grouped.map((g) => g.items.length > 0 && (
        <section key={g.type} className="app-card p-5">
          <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
            <g.icon className="h-4 w-4 brand" /> {t(TYPE_META[g.type].label)}
          </h3>
          <ul className="divide-y divide-[var(--border-color)]">
            {g.items.map((f) => (
              <li key={f.id} className="flex items-center gap-3 py-2.5" data-testid={`favorite-row-${f.id}`}>
                <button className="flex-1 text-left text-sm hover:underline truncate" onClick={() => open(f)} data-testid={`favorite-open-${f.id}`}>
                  {f.name}
                </button>
                <button onClick={() => remove(f.id)} data-testid={`favorite-remove-${f.id}`}>
                  <Heart className="h-4 w-4 fill-rose-400 text-rose-400 hover:fill-transparent transition-colors" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
